"use client";

import { c as _c } from "react/compiler-runtime";
import { CopyIcon, ExternalLinkIcon } from "lucide-react";
import { ContextMenuContent, ContextMenuCheckboxItem, ContextMenuItem, ContextMenuRadioGroup, ContextMenuRadioItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger } from "@/src/components/shadcn/ui/context-menu";
import { DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuItem, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import { useToast } from "@/src/hooks/use-toast";
export enum MenuActionType {
  Item = "item",
  Radio = "radio",
  Checkbox = "checkbox",
  CustomSubmenu = "custom_submenu",
}
export type ItemAction = {
  type: MenuActionType.Item;
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  shortcut?: string;
};
export type SubmenuRadioOption = {
  value: string;
  label: string;
  content?: React.ReactNode;
};
export type SubmenuRadioAction = {
  type: MenuActionType.Radio;
  label: string;
  icon?: React.ReactNode;
  value: string | undefined;
  options: SubmenuRadioOption[];
  onValueChange?: (value: string) => void;
  subContentClassName?: string;
};
export type SubmenuCheckboxOption = {
  value: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  content?: React.ReactNode;
};
export type SubmenuCheckboxAction = {
  type: MenuActionType.Checkbox;
  label: string;
  icon?: React.ReactNode;
  options: SubmenuCheckboxOption[];
  onCheckedChange?: (value: string, checked: boolean) => void;
  subContentClassName?: string;
};
export type SubmenuCustomAction = {
  type: MenuActionType.CustomSubmenu;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  subContentClassName?: string;
};
export type MenuAction = ItemAction | SubmenuRadioAction | SubmenuCheckboxAction | SubmenuCustomAction;
export type ActionGroup = MenuAction[];
export type EntityMenuEntity = {
  href?: string;
  id: string;
  label: string;
};
interface EntityMenuProps {
  isContextMenu?: boolean;
  entity: EntityMenuEntity;
  actionGroups: ActionGroup[];
  align?: "start" | "center" | "end";
  className?: string;
  onCloseAutoFocus?: (e: Event) => void;
  showCopyId?: boolean;
}
export function EntityMenu(t0) {
  const $ = _c(39);
  const {
    isContextMenu: t1,
    entity,
    actionGroups,
    align: t2,
    className,
    onCloseAutoFocus,
    showCopyId: t3
  } = t0;
  const isContextMenu = t1 === undefined ? false : t1;
  const align = t2 === undefined ? "end" : t2;
  const showCopyId = t3 === undefined ? true : t3;
  const {
    toast
  } = useToast();
  const MenuContent = isContextMenu ? ContextMenuContent : DropdownMenuContent;
  const Item = isContextMenu ? ContextMenuItem : DropdownMenuItem;
  const CheckboxItem = isContextMenu ? ContextMenuCheckboxItem : DropdownMenuCheckboxItem;
  const Separator = isContextMenu ? ContextMenuSeparator : DropdownMenuSeparator;
  const Shortcut = isContextMenu ? ContextMenuShortcut : DropdownMenuShortcut;
  const Sub = isContextMenu ? ContextMenuSub : DropdownMenuSub;
  const SubContent = isContextMenu ? ContextMenuSubContent : DropdownMenuSubContent;
  const SubTrigger = isContextMenu ? ContextMenuSubTrigger : DropdownMenuSubTrigger;
  const RadioGroup = isContextMenu ? ContextMenuRadioGroup : DropdownMenuRadioGroup;
  const RadioItem = isContextMenu ? ContextMenuRadioItem : DropdownMenuRadioItem;
  const renderLabel = _temp;
  let t4;
  if ($[0] !== toast) {
    t4 = (id, label_0) => {
      const doCopy = async () => {
        try {
          await navigator.clipboard.writeText(id);
          toast({
            title: `Copied ${label_0} to clipboard`
          });
        } catch {
          toast({
            title: "Error",
            description: `Failed to copy ${label_0}`,
            variant: "destructive"
          });
        }
      };
      doCopy();
    };
    $[0] = toast;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleCopyId = t4;
  let t5;
  if ($[2] !== CheckboxItem || $[3] !== Item || $[4] !== RadioGroup || $[5] !== RadioItem || $[6] !== Shortcut || $[7] !== Sub || $[8] !== SubContent || $[9] !== SubTrigger) {
    t5 = (action, groupIndex, actionIndex) => {
      if (action.type === MenuActionType.Item) {
        return [<Item key={`${groupIndex}-${actionIndex}`} onClick={action.onClick} disabled={action.disabled}>{renderLabel(action.label, action.icon)}{action.shortcut ? <Shortcut>{action.shortcut}</Shortcut> : null}</Item>];
      }
      if (action.type === MenuActionType.Radio) {
        return [<Sub key={`${groupIndex}-${actionIndex}`}><SubTrigger>{renderLabel(action.label, action.icon)}</SubTrigger><SubContent className={action.subContentClassName}><RadioGroup value={action.value} onValueChange={action.onValueChange}>{action.options.map(opt => <RadioItem key={opt.value} value={opt.value}>{opt.content ?? opt.label}</RadioItem>)}</RadioGroup></SubContent></Sub>];
      }
      if (action.type === MenuActionType.Checkbox) {
        return [<Sub key={`${groupIndex}-${actionIndex}`}><SubTrigger>{renderLabel(action.label, action.icon)}</SubTrigger><SubContent className={action.subContentClassName}>{action.options.map(opt_0 => <CheckboxItem key={opt_0.value} checked={opt_0.checked} disabled={opt_0.disabled} onCheckedChange={checked => action.onCheckedChange?.(opt_0.value, checked === true)}>{opt_0.content ?? opt_0.label}</CheckboxItem>)}</SubContent></Sub>];
      }
      if (action.type === MenuActionType.CustomSubmenu) {
        return [<Sub key={`${groupIndex}-${actionIndex}`}><SubTrigger>{renderLabel(action.label, action.icon)}</SubTrigger><SubContent className={action.subContentClassName}>{action.content}</SubContent></Sub>];
      }
      return [];
    };
    $[2] = CheckboxItem;
    $[3] = Item;
    $[4] = RadioGroup;
    $[5] = RadioItem;
    $[6] = Shortcut;
    $[7] = Sub;
    $[8] = SubContent;
    $[9] = SubTrigger;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const expandAction = t5;
  let t6;
  if ($[11] !== Item || $[12] !== entity.href) {
    t6 = entity.href != null && <Item onClick={() => window.open(entity.href, "_blank", "noopener,noreferrer")}>{renderLabel("Open in New Tab", <ExternalLinkIcon className="h-4 w-4 text-muted-foreground" />)}</Item>;
    $[11] = Item;
    $[12] = entity.href;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== Item || $[15] !== entity.id || $[16] !== entity.label || $[17] !== handleCopyId || $[18] !== showCopyId) {
    t7 = showCopyId && <Item onClick={() => handleCopyId(entity.id, entity.label)}>{renderLabel(`Copy ${entity.label}`, <CopyIcon className="h-4 w-4 text-muted-foreground" />)}</Item>;
    $[14] = Item;
    $[15] = entity.id;
    $[16] = entity.label;
    $[17] = handleCopyId;
    $[18] = showCopyId;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== Separator || $[21] !== actionGroups || $[22] !== entity.href || $[23] !== expandAction || $[24] !== showCopyId) {
    let t9;
    if ($[26] !== Separator || $[27] !== entity.href || $[28] !== expandAction || $[29] !== showCopyId) {
      t9 = (group, groupIndex_0) => <span key={groupIndex_0}>{(entity.href != null || showCopyId) && <Separator />}{group.flatMap((action_0, actionIndex_0) => expandAction(action_0, groupIndex_0, actionIndex_0))}</span>;
      $[26] = Separator;
      $[27] = entity.href;
      $[28] = expandAction;
      $[29] = showCopyId;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    t8 = actionGroups.map(t9);
    $[20] = Separator;
    $[21] = actionGroups;
    $[22] = entity.href;
    $[23] = expandAction;
    $[24] = showCopyId;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[31] !== MenuContent || $[32] !== align || $[33] !== className || $[34] !== onCloseAutoFocus || $[35] !== t6 || $[36] !== t7 || $[37] !== t8) {
    t9 = <MenuContent align={align} className={className} onClick={_temp2} onCloseAutoFocus={onCloseAutoFocus}>{t6}{t7}{t8}</MenuContent>;
    $[31] = MenuContent;
    $[32] = align;
    $[33] = className;
    $[34] = onCloseAutoFocus;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  return t9;
}
function _temp2(e) {
  return e.stopPropagation();
}
function _temp(label, icon) {
  return <span className="flex w-full items-center">{icon ? <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">{icon}</span> : null}<span className="truncate">{label}</span></span>;
}